/**
 * Visualizations module
 * Handles word clouds (3D with Three.js) and tag explorer
 */

import { API } from '../core/api.js';

// Color palette for word types
const TYPE_COLORS = {
    keyword: 0x3b82f6,    // Blue
    concept: 0x10b981,    // Green
    entity: 0xf59e0b,     // Orange
    entity_person: 0xef4444,  // Red
    entity_organization: 0x8b5cf6,  // Purple
    entity_location: 0x06b6d4,  // Cyan
    tag: 0xec4899         // Pink
};

// State for 3D visualization
let scene, camera, renderer, wordSprites = [];
let isRotating = true;
let animationId = null;
let mouseX = 0, mouseY = 0;

/**
 * Show podcast word cloud visualization in a modal
 */
export async function showPodcastVisualizations(podcastId) {
    try {
        const data = await API.get(`/api/podcasts/${podcastId}/word-cloud`);

        if (!data.words || data.words.length === 0) {
            alert('No word cloud data available for this podcast yet. Process some episodes first.');
            return;
        }

        showVisualizationModal(data, 'podcast');
    } catch (error) {
        console.error('Error loading podcast word cloud:', error);
        alert('Failed to load word cloud data');
    }
}

/**
 * Show episode word cloud visualization
 */
export async function showEpisodeWordCloud(episodeId) {
    try {
        const data = await API.get(`/api/episodes/${episodeId}/word-cloud`);

        if (!data.words || data.words.length === 0) {
            alert('No word cloud data available for this episode. It may not have been analyzed yet.');
            return;
        }

        showVisualizationModal(data, 'episode');
    } catch (error) {
        console.error('Error loading episode word cloud:', error);
        alert('Failed to load word cloud data');
    }
}

/**
 * Show tag explorer
 */
export async function showTagExplorer() {
    try {
        const data = await API.get('/api/tags/explorer');

        if (!data.tags || data.tags.length === 0) {
            alert('No tags found. Process some episodes first.');
            return;
        }

        showTagExplorerModal(data);
    } catch (error) {
        console.error('Error loading tag explorer:', error);
        alert('Failed to load tags');
    }
}

/**
 * Create an element with attributes and children
 */
function createElement(tag, attrs = {}, children = []) {
    const el = document.createElement(tag);
    Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'className') {
            el.className = value;
        } else if (key === 'style' && typeof value === 'object') {
            Object.assign(el.style, value);
        } else if (key.startsWith('data-')) {
            el.dataset[key.slice(5)] = value;
        } else {
            el.setAttribute(key, value);
        }
    });
    children.forEach(child => {
        if (typeof child === 'string') {
            el.appendChild(document.createTextNode(child));
        } else if (child) {
            el.appendChild(child);
        }
    });
    return el;
}

/**
 * Create and show the visualization modal
 */
function showVisualizationModal(data, type) {
    // Remove existing modal if any
    const existingModal = document.getElementById('visualization-modal');
    if (existingModal) {
        cleanupVisualization();
        existingModal.remove();
    }

    const title = type === 'podcast' ? data.podcast_title : data.episode_title;

    // Build modal using DOM methods
    const modal = createElement('div', { id: 'visualization-modal', className: 'viz-modal' });

    const content = createElement('div', { className: 'viz-modal-content' });

    // Header
    const header = createElement('div', { className: 'viz-modal-header' });
    const h2 = createElement('h2', {}, [`Word Cloud: ${title}`]);
    const controls = createElement('div', { className: 'viz-controls' });

    const rotateBtn = createElement('button', { id: 'viz-toggle-rotate', className: 'viz-btn active', title: 'Toggle rotation' });
    rotateBtn.innerHTML = '<span class="viz-icon">&#8634;</span>';

    const btn2d = createElement('button', { id: 'viz-toggle-2d', className: 'viz-btn', title: 'Switch to 2D view' }, ['2D']);
    const btn3d = createElement('button', { id: 'viz-toggle-3d', className: 'viz-btn active', title: 'Switch to 3D view' }, ['3D']);
    const closeBtn = createElement('button', { id: 'viz-close', className: 'viz-btn-close' }, ['\u00D7']);

    controls.append(rotateBtn, btn2d, btn3d, closeBtn);
    header.append(h2, controls);

    // Legend
    const legend = createElement('div', { className: 'viz-legend' });
    const legendItems = [
        { label: 'Keywords', color: '#3b82f6' },
        { label: 'Concepts', color: '#10b981' },
        { label: 'Entities', color: '#f59e0b' },
        { label: 'Tags', color: '#ec4899' }
    ];
    legendItems.forEach(item => {
        const span = createElement('span', { className: 'viz-legend-item' }, [item.label]);
        span.style.setProperty('--dot-color', item.color);
        legend.appendChild(span);
    });

    // Container
    const vizContainer = createElement('div', { id: 'viz-container', className: 'viz-container' });
    const container3d = createElement('div', { id: 'viz-3d-container', className: 'viz-3d-container' });
    const container2d = createElement('div', { id: 'viz-2d-container', className: 'viz-2d-container', style: { display: 'none' } });
    vizContainer.append(container3d, container2d);

    content.append(header, legend, vizContainer);

    // Stats for podcast view
    if (type === 'podcast' && data.totals) {
        const stats = createElement('div', { className: 'viz-stats' }, [
            createElement('span', {}, [`Keywords: ${data.totals.keywords}`]),
            createElement('span', {}, [`Concepts: ${data.totals.concepts}`]),
            createElement('span', {}, [`Entities: ${data.totals.entities}`]),
            createElement('span', {}, [`Tags: ${data.totals.tags}`])
        ]);
        content.appendChild(stats);
    }

    modal.appendChild(content);
    document.body.appendChild(modal);

    // Initialize 3D visualization
    init3DWordCloud(data.words);

    // Also prepare 2D view
    render2DWordCloud(data.words, container2d);

    // Event listeners
    closeBtn.addEventListener('click', () => {
        cleanupVisualization();
        modal.remove();
    });

    rotateBtn.addEventListener('click', (e) => {
        isRotating = !isRotating;
        e.currentTarget.classList.toggle('active', isRotating);
    });

    btn2d.addEventListener('click', () => {
        container3d.style.display = 'none';
        container2d.style.display = 'flex';
        btn2d.classList.add('active');
        btn3d.classList.remove('active');
    });

    btn3d.addEventListener('click', () => {
        container3d.style.display = 'block';
        container2d.style.display = 'none';
        btn3d.classList.add('active');
        btn2d.classList.remove('active');
    });

    // Close on escape
    const handleEscape = (e) => {
        if (e.key === 'Escape') {
            cleanupVisualization();
            modal.remove();
            document.removeEventListener('keydown', handleEscape);
        }
    };
    document.addEventListener('keydown', handleEscape);

    // Close on backdrop click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            cleanupVisualization();
            modal.remove();
        }
    });
}

/**
 * Initialize Three.js 3D word cloud
 */
function init3DWordCloud(words) {
    const container = document.getElementById('viz-3d-container');
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene setup
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);

    // Camera
    camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 300;

    // Renderer
    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);

    // Create word sprites
    const maxCount = Math.max(...words.map(w => w.count), 1);

    words.forEach((wordData, index) => {
        const sprite = createTextSprite(wordData, maxCount, index, words.length);
        scene.add(sprite);
        wordSprites.push(sprite);
    });

    // Mouse interaction
    container.addEventListener('mousemove', onMouseMove);

    // Handle resize
    window.addEventListener('resize', onWindowResize);

    // Start animation
    animate();
}

/**
 * Create a text sprite for 3D display
 */
function createTextSprite(wordData, maxCount, index, totalWords) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Calculate size based on count (normalized)
    const sizeRatio = Math.sqrt(wordData.count / maxCount);
    const fontSize = Math.max(16, Math.min(64, 16 + sizeRatio * 48));

    canvas.width = 512;
    canvas.height = 128;

    context.font = `bold ${fontSize}px Arial, sans-serif`;
    context.fillStyle = '#' + (TYPE_COLORS[wordData.type] || TYPE_COLORS.keyword).toString(16).padStart(6, '0');
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(wordData.word, 256, 64);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true
    });

    const sprite = new THREE.Sprite(material);

    // Position on a sphere
    const phi = Math.acos(-1 + (2 * index) / totalWords);
    const theta = Math.sqrt(totalWords * Math.PI) * phi;
    const radius = 150 + Math.random() * 50;

    sprite.position.x = radius * Math.cos(theta) * Math.sin(phi);
    sprite.position.y = radius * Math.sin(theta) * Math.sin(phi);
    sprite.position.z = radius * Math.cos(phi);

    sprite.scale.set(100, 25, 1);

    // Store original position for animation
    sprite.userData = {
        originalX: sprite.position.x,
        originalY: sprite.position.y,
        originalZ: sprite.position.z,
        word: wordData.word,
        count: wordData.count,
        type: wordData.type
    };

    return sprite;
}

/**
 * Animation loop
 */
function animate() {
    animationId = requestAnimationFrame(animate);

    if (isRotating) {
        // Gentle auto-rotation
        wordSprites.forEach(sprite => {
            const userData = sprite.userData;
            const time = Date.now() * 0.0001;

            // Rotate around Y axis
            const cos = Math.cos(time);
            const sin = Math.sin(time);
            sprite.position.x = userData.originalX * cos - userData.originalZ * sin;
            sprite.position.z = userData.originalX * sin + userData.originalZ * cos;
        });
    }

    // Mouse influence
    const targetX = mouseX * 0.001;
    const targetY = mouseY * 0.001;
    camera.rotation.y += (targetX - camera.rotation.y) * 0.05;
    camera.rotation.x += (-targetY - camera.rotation.x) * 0.05;

    renderer.render(scene, camera);
}

/**
 * Handle mouse movement
 */
function onMouseMove(event) {
    const container = document.getElementById('viz-3d-container');
    if (!container) return;

    const rect = container.getBoundingClientRect();
    mouseX = (event.clientX - rect.left - rect.width / 2);
    mouseY = (event.clientY - rect.top - rect.height / 2);
}

/**
 * Handle window resize
 */
function onWindowResize() {
    const container = document.getElementById('viz-3d-container');
    if (!container || !camera || !renderer) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
}

/**
 * Cleanup Three.js resources
 */
function cleanupVisualization() {
    if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
    }

    wordSprites.forEach(sprite => {
        if (sprite.material.map) sprite.material.map.dispose();
        sprite.material.dispose();
    });
    wordSprites = [];

    if (renderer) {
        renderer.dispose();
        renderer = null;
    }

    scene = null;
    camera = null;
    isRotating = true;

    window.removeEventListener('resize', onWindowResize);
}

/**
 * Render 2D word cloud (CSS-based fallback)
 */
function render2DWordCloud(words, container) {
    if (!container) return;

    container.textContent = ''; // Clear safely

    const maxCount = Math.max(...words.map(w => w.count), 1);

    words.forEach(wordData => {
        const sizeRatio = Math.sqrt(wordData.count / maxCount);
        const fontSize = Math.max(12, Math.min(48, 12 + sizeRatio * 36));
        const color = '#' + (TYPE_COLORS[wordData.type] || TYPE_COLORS.keyword).toString(16).padStart(6, '0');

        const span = createElement('span', {
            className: 'viz-word viz-word-clickable',
            title: `${wordData.word} (${wordData.count}) - Click to see episodes`,
            style: { fontSize: `${fontSize}px`, color: color, cursor: 'pointer' }
        }, [wordData.word]);

        // Store episode data for click handler
        span.addEventListener('click', (e) => {
            e.stopPropagation();
            showWordEpisodes(wordData, e.target);
        });

        container.appendChild(span);
        container.appendChild(document.createTextNode(' '));
    });
}

/**
 * Show episodes popup for a clicked word
 */
function showWordEpisodes(wordData, targetElement) {
    // Remove any existing popup
    const existingPopup = document.getElementById('word-episodes-popup');
    if (existingPopup) existingPopup.remove();

    const episodes = wordData.episodes || [];

    if (episodes.length === 0) {
        // No episode data, navigate to search
        const modal = document.getElementById('visualization-modal');
        if (modal) modal.remove();
        cleanupVisualization();
        window.location.hash = `#/search?q=${encodeURIComponent(wordData.word)}`;
        return;
    }

    // Create popup
    const popup = createElement('div', {
        id: 'word-episodes-popup',
        className: 'word-episodes-popup'
    });

    // Header
    const header = createElement('div', { className: 'word-popup-header' });
    const title = createElement('span', { className: 'word-popup-title' }, [
        `"${wordData.word}" (${wordData.count} mentions)`
    ]);
    const closeBtn = createElement('button', { className: 'word-popup-close' }, ['×']);
    closeBtn.onclick = () => popup.remove();
    header.append(title, closeBtn);

    // Episode list
    const list = createElement('div', { className: 'word-popup-list' });

    episodes.slice(0, 10).forEach(ep => {
        const item = createElement('div', { className: 'word-popup-episode' });
        const link = createElement('a', {
            href: `#/episode/${ep.id}`,
            className: 'word-popup-link'
        }, [ep.title]);

        link.onclick = (e) => {
            e.preventDefault();
            popup.remove();
            const modal = document.getElementById('visualization-modal');
            if (modal) {
                cleanupVisualization();
                modal.remove();
            }
            window.location.hash = `#/episode/${ep.id}`;
        };

        item.appendChild(link);
        list.appendChild(item);
    });

    if (episodes.length > 10) {
        const more = createElement('div', { className: 'word-popup-more' }, [
            `+ ${episodes.length - 10} more episodes`
        ]);
        list.appendChild(more);
    }

    // Search all button
    const searchBtn = createElement('button', { className: 'word-popup-search btn btn-sm' }, [
        'Search all episodes'
    ]);
    searchBtn.onclick = () => {
        popup.remove();
        const modal = document.getElementById('visualization-modal');
        if (modal) {
            cleanupVisualization();
            modal.remove();
        }
        window.location.hash = `#/search?q=${encodeURIComponent(wordData.word)}`;
    };

    popup.append(header, list, searchBtn);

    // Position popup near the clicked word
    const rect = targetElement.getBoundingClientRect();
    popup.style.position = 'fixed';
    popup.style.left = `${Math.min(rect.left, window.innerWidth - 320)}px`;
    popup.style.top = `${Math.min(rect.bottom + 5, window.innerHeight - 300)}px`;

    document.body.appendChild(popup);

    // Close on click outside
    const closeOnOutside = (e) => {
        if (!popup.contains(e.target) && e.target !== targetElement) {
            popup.remove();
            document.removeEventListener('click', closeOnOutside);
        }
    };
    setTimeout(() => document.addEventListener('click', closeOnOutside), 100);
}

/**
 * Show tag explorer modal
 */
function showTagExplorerModal(data) {
    // Remove existing modal if any
    const existingModal = document.getElementById('tag-explorer-modal');
    if (existingModal) existingModal.remove();

    const modal = createElement('div', { id: 'tag-explorer-modal', className: 'viz-modal' });
    const content = createElement('div', { className: 'viz-modal-content tag-explorer-content' });

    // Header
    const header = createElement('div', { className: 'viz-modal-header' });
    const h2 = createElement('h2', {}, ['Tag Explorer']);
    const closeBtn = createElement('button', { id: 'tag-explorer-close', className: 'viz-btn-close' }, ['\u00D7']);
    header.append(h2, closeBtn);

    // Stats
    const stats = createElement('div', { className: 'tag-explorer-stats' }, [
        createElement('span', {}, [`Total Tags: ${data.total_tags}`])
    ]);

    // Grid
    const grid = createElement('div', { className: 'tag-explorer-grid' });

    data.tags.forEach(tag => {
        const item = createElement('div', { className: 'tag-explorer-item', 'data-tag': tag.tag });

        const nameSpan = createElement('span', { className: 'tag-name' }, [tag.tag]);
        const countSpan = createElement('span', { className: 'tag-count' }, [String(tag.count)]);

        const podcastsText = tag.podcasts.slice(0, 3).join(', ') + (tag.podcasts.length > 3 ? '...' : '');
        const podcastsDiv = createElement('div', { className: 'tag-podcasts' }, [podcastsText]);

        item.append(nameSpan, countSpan, podcastsDiv);

        item.addEventListener('click', () => {
            modal.remove();
            window.location.hash = `#/search?q=tag:${encodeURIComponent(tag.tag)}`;
        });

        grid.appendChild(item);
    });

    content.append(header, stats, grid);
    modal.appendChild(content);
    document.body.appendChild(modal);

    // Event listeners
    closeBtn.addEventListener('click', () => modal.remove());

    // Close on escape
    const handleEscape = (e) => {
        if (e.key === 'Escape') {
            modal.remove();
            document.removeEventListener('keydown', handleEscape);
        }
    };
    document.addEventListener('keydown', handleEscape);

    // Close on backdrop click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            modal.remove();
        }
    });
}

// Export functions for global access
window.showPodcastVisualizations = showPodcastVisualizations;
window.showEpisodeWordCloud = showEpisodeWordCloud;
window.showTagExplorer = showTagExplorer;
